import { fetchRaw, get } from "./client";
import type { Website } from "../constants/features.js";

const FIRM_LOGO_FILL_ENDPOINT = "/v1/wp-syncer/firm-logo-fill";
const FIRM_LOGO_FILL_EXPORT_ENDPOINT = "/v1/wp-syncer/firm-logo-fill/export";

export const FIRM_LOGO_FILL_REVIEW_REASONS = ["profile-logo-clash", "already-set"] as const;

export const FIRM_LOGO_FILL_NOTE_REASONS = ["no-firm-wide"] as const;

export const FIRM_LOGO_FILL_REASONS = [
  "write",
  "already-same",
  ...FIRM_LOGO_FILL_REVIEW_REASONS,
  ...FIRM_LOGO_FILL_NOTE_REASONS,
] as const;

export type FirmLogoFillReason = (typeof FIRM_LOGO_FILL_REASONS)[number];

export const FIRM_LOGO_FILL_KINDS = ["write", "unchanged", "review", "note"] as const;

export type FirmLogoFillKind = (typeof FIRM_LOGO_FILL_KINDS)[number];

export type FirmLogoFillCandidate = {
  logoHash: string;
  logoFilename: string;
  locations: string[];
};

export type FirmLogoFillCurrentLogo = {
  logoHash: string;
  logoFilename: string;
  source: string;
  updatedAt: string;
};

export type FirmLogoFillItem = {
  companyId: string;
  companyWordpressId: number | null;
  companyName: string | null;
  website: Website;
  countryId: number | null;
  countryName: string | null;
  reason: string;
  kind: string;
  logoHash: string | null;
  logoFilename: string | null;
  plannedLogoUrl: string | null;
  candidates: FirmLogoFillCandidate[];
  currentLogo: FirmLogoFillCurrentLogo | null;
};

export type FirmLogoFillSummary = {
  totalRows: number;
  byReason: Partial<Record<string, number>>;
  byKind: Partial<Record<string, number>>;
  resolvedByReason: Partial<Record<string, number>>;
  resolvedByKind: Partial<Record<string, number>>;
};

export type FirmLogoFillRunState = {
  state: "never" | "applied" | "undone";
  at: string | null;
  label: string | null;
};

export type FirmLogoFillResponse = {
  summary: FirmLogoFillSummary;
  lastRun: FirmLogoFillRunState;
  page: number;
  pageSize: number;
  totalItems: number;
  items: FirmLogoFillItem[];
  request_id: string;
};

export type FirmLogoFillFilter = {
  reason?: string;
  website?: Website;
  company?: string;
  unresolved?: boolean;
};

export type FirmLogoFillQuery = FirmLogoFillFilter & {
  page?: number;
  pageSize?: number;
};

export type FirmLogoFillExportFormat = "csv" | "json";

export function isFirmLogoFillReason(value: string | null): value is FirmLogoFillReason {
  return FIRM_LOGO_FILL_REASONS.some(reason => reason === value);
}

function withQueryString(endpoint: string, query: FirmLogoFillQuery & { format?: string }): string {
  const params = new URLSearchParams();

  if (query.reason) {
    params.set("reason", query.reason);
  }

  if (query.website) {
    params.set("website", query.website);
  }

  if (query.company) {
    params.set("company", query.company);
  }

  if (query.unresolved) {
    params.set("unresolved", "true");
  }

  if (query.page !== undefined) {
    params.set("page", String(query.page));
  }

  if (query.pageSize !== undefined) {
    params.set("pageSize", String(query.pageSize));
  }

  if (query.format) {
    params.set("format", query.format);
  }

  const queryString = params.toString();

  return `${endpoint}${queryString ? `?${queryString}` : ""}`;
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function readErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("application/json")) {
    return `HTTP ${response.status}`;
  }

  const body = await readJsonBody(response);

  if (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
  ) {
    return body.error;
  }

  return `HTTP ${response.status}`;
}

async function list(query: FirmLogoFillQuery): Promise<FirmLogoFillResponse> {
  return get(withQueryString(FIRM_LOGO_FILL_ENDPOINT, query));
}

async function exportFile(
  format: FirmLogoFillExportFormat,
  filter: FirmLogoFillFilter,
): Promise<Blob> {
  const response = await fetchRaw(
    withQueryString(FIRM_LOGO_FILL_EXPORT_ENDPOINT, { ...filter, format }),
  );

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return response.blob();
}

export const firmLogoFillService = { list, exportFile };
